import React, { useState, FC } from 'react';

import Box from '@mui/material/Box';
import cx from 'classnames';
import { useLocation } from 'react-router-dom';

import ChevronTopIcon from '../../../../assets/icons/ChevronTopIcon';
import styled from '../../../../styled';
import { colorsPalette, SMALL_MARGIN_PX } from '../../../../theme';
import { NavigationItem } from '../../../../types';
import { ExpandedNavigationItem } from '../../const';
import MegaMenu from '../MegaMenu';
import SafeLink from '../SafeLink/SafeLink';
import useStyles from './styles';

interface MenuItemProps {
  item: NavigationItem | ExpandedNavigationItem;
  isMobile: boolean;
  hasSubscription: boolean;
  wrapperClass?: string;
  isManager?: boolean | null;
  handleClickLink?: () => void;
  scrolled: boolean;
}

function isExpandedNavigationItem(
  pet: NavigationItem | ExpandedNavigationItem,
): pet is ExpandedNavigationItem {
  return (pet as ExpandedNavigationItem).subItems !== undefined;
}

const MenuItem: FC<MenuItemProps> = ({
  isManager,
  isMobile,
  hasSubscription,
  item,
  wrapperClass,
  handleClickLink,
  scrolled,
}) => {
  const isMegaMenu = item?.type === 'MegaMenu';
  const classes = useStyles(
    isExpandedNavigationItem(item) && item?.subItems?.length && item?.subItems?.length > 4,
    isMegaMenu,
  )();

  const location = useLocation();
  const pathname = location?.pathname;

  const managerLink = isExpandedNavigationItem(item) && item?.managerUrl;
  const avoidManagerUrl =
    isExpandedNavigationItem(item) && item?.avoidManagerUrlWithoutSub && !hasSubscription;
  const link = isManager && managerLink && !avoidManagerUrl ? managerLink : item?.url;
  const isActive = pathname === item?.url || pathname === managerLink;

  const [submenuOpened, setSubmenuOpened] = useState(false);

  const toggleSubmenu = () => {
    if (!isMobile) return;
    setSubmenuOpened((prevState) => !prevState);
  };

  return (
    <Box
      className={cx(
        classes.linkWrapper,
        isMegaMenu && 'hasSubItems',
        wrapperClass,
        isExpandedNavigationItem(item) && item?.className ? item.className : '',
      )}
    >
      <LinkWrap isSubmenuOpened={isMegaMenu && submenuOpened}>
        <Link
          to={link}
          isSubmenuOpened={isMegaMenu && submenuOpened}
          className={cx(isActive ? 'active' : '', isMegaMenu && 'withSubItems')}
          onClick={() => handleClickLink && handleClickLink()}
        >
          {item?.title}
        </Link>
        {isMegaMenu && (
          <ChevronTopIcon
            color={
              isMobile
                ? submenuOpened
                  ? colorsPalette.primary
                  : colorsPalette.white
                : colorsPalette.white
            }
            className={cx(classes.linkIcon, submenuOpened && 'opened')}
            onClick={toggleSubmenu}
          />
        )}
      </LinkWrap>
      {isMegaMenu && (
        <Submenu className="sublistList" opened={submenuOpened} scrolled={scrolled}>
          {/*@ts-ignore*/}
          <MegaMenu blocks={item?.blocks} />
        </Submenu>
      )}
    </Box>
  );
};

export default MenuItem;

const LinkWrap = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isSubmenuOpened',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ isSubmenuOpened }) =>
    isSubmenuOpened ? colorsPalette.blackSecondary : colorsPalette.blueDark};
  margin: 0 -24px;
  padding: 0 24px;
  width: 100vw;
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: 0 -30px;
    padding: 0 30px;
  }
  ${({ theme }) => theme.breakpoints.up('xl')} {
    justify-content: flex-start;
    width: auto;
    background: transparent;
    padding: unset;
    margin: unset;
  }
`;

const Link = styled(SafeLink, {
  shouldForwardProp: (prop) => prop !== 'isSubmenuOpened',
})`
  text-decoration: none;
  text-transform: uppercase;
  font-size: 18px;
  font-weight: bold;
  color: ${({ isSubmenuOpened }) =>
    isSubmenuOpened ? colorsPalette.primary : colorsPalette.white};
  display: block;
  transition: 0.3s;
  padding: ${SMALL_MARGIN_PX} 0;
  margin-left: 0;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('xl')} {
    font-size: 16px;
    color: ${colorsPalette.white};
    padding: 0;
    text-align: center;
    display: flex;
    align-items: center;
    color: inherit;
    font-weight: bold;
    position: relative;
    &.withSubItems {
      z-index: 10;
    }
    &:not(.withSubItems):after {
      content: '';
      position: absolute;
      top: 120%;
      height: 3px;
      width: 0%;
      left: 1%;
      background-color: ${({ theme }) => theme.palette.primary.main};
      transition: 0.5s ease all 0.3s;
    }
    &:after {
      width: 99%;
      transition: 0.3s ease all;
    }
    &:not(.withSubItems).active:after {
      width: 99%;
    }
  }
  ${({ theme }) => theme.breakpoints.between('sm', 'lg')} {
    font-size: 16px;
  }
`;

const Submenu = styled('div', {
  shouldForwardProp: (prop) => prop !== 'opened' && prop !== 'scrolled',
})`
  display: ${({ opened }) => (opened ? 'block' : 'none')};
  width: 100%;
  transition: all 0.5s ease-in-out;
  ${({ theme }) => theme.breakpoints.up('xl')} {
    position: fixed;
    top: ${({ scrolled }) => (scrolled ? '68' : '85')}px;
    left: 0;
    width: 100vw;
  }
`;
